import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, ActivityIndicator } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Svg, Path } from 'react-native-svg';
import { MeetingsService } from '@/services/meetingsService';
import { Meeting } from '@/types/meetings';
import { MeetingGroup } from '@/types/groups';
import { getMeetingTimeStatus, getMeetingDisplayTime } from '@/utils/meetingUtils';
import FilterDrawer from '@/components/FilterDrawer';
import TimeFilterContent from '@/components/TimeFilterContent';
import DayFilterContent from '@/components/DayFilterContent';
import CategoryFilterContent, { type CategoryOption } from '@/components/CategoryFilterContent';
import FilterChip, { ClockChipIcon, CalendarChipIcon, CategoryChipIcon } from '@/components/FilterChip';
import { translateCategory } from '@/utils/meetingLocale';
import { ImageBackground } from 'react-native';
import { Skeleton, SkeletonCircle } from '@/components/Skeleton';
import { useLanguage } from '@/contexts/LanguageContext';
import { translateGroup, translateMeetings } from '@/utils/meetingTranslation';
import PageHeader from '@/components/PageHeader';

export default function GroupPage() {
  const router = useRouter();
  const { t, language } = useLanguage();
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const [group, setGroup] = useState<MeetingGroup | null>(null);
  const [translatedGroup, setTranslatedGroup] = useState<MeetingGroup | null>(null);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTimes, setSelectedTimes] = useState<string[]>([]);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [tempSelectedTimes, setTempSelectedTimes] = useState<string[]>([]);
  const [tempSelectedDays, setTempSelectedDays] = useState<string[]>([]);
  const [tempSelectedCategories, setTempSelectedCategories] = useState<string[]>([]);
  const [timeFilterVisible, setTimeFilterVisible] = useState(false);
  const [dayFilterVisible, setDayFilterVisible] = useState(false);
  const [categoryFilterVisible, setCategoryFilterVisible] = useState(false);

  useEffect(() => {
    fetchGroupAndMeetings();
  }, [slug, language]);

  const fetchGroupAndMeetings = async () => {
    try {
      setLoading(true);

      const languageMap: Record<string, string> = { 'pt': 'pt-BR', 'en': 'en-US', 'es': 'es-ES' };
      const targetLanguage = languageMap[language] || 'pt-BR';

      console.log('📋 Fetching group with slug via API:', slug);

      const groupData = await MeetingsService.getGroupBySlug(slug || '');

      if (!groupData) {
        console.error('❌ Group not found for slug:', slug);
        setGroup(null);
        return;
      }

      console.log('✅ Group found via API:', groupData.name);

      // Fetch meetings and translate group description in parallel
      const [meetingsData, translatedGroupData] = await Promise.all([
        MeetingsService.getMeetingsByGroupId(groupData.id),
        translateGroup(groupData, targetLanguage),
      ]);

      console.log(`✅ Found ${meetingsData.length} meetings for this group via API`);

      const translatedMeetingsData = await translateMeetings(meetingsData, targetLanguage);

      setGroup(groupData);
      setTranslatedGroup(translatedGroupData);
      setMeetings(translatedMeetingsData);
    } catch (error) {
      console.error('❌ Error in fetchGroupAndMeetings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMeetingPress = (meeting: Meeting) => {
    router.push(`/meeting/${meeting.id}`);
  };

  const handleTimeFilterPress = () => {
    setTempSelectedTimes(selectedTimes);
    setTimeFilterVisible(true);
  };

  const handleDayFilterPress = () => {
    setTempSelectedDays(selectedDays);
    setDayFilterVisible(true);
  };

  const handleCategoryFilterPress = () => {
    setTempSelectedCategories(selectedCategories);
    setCategoryFilterVisible(true);
  };

  const applyTimeFilters = () => {
    setSelectedTimes(tempSelectedTimes);
    setTimeFilterVisible(false);
  };

  const applyDayFilters = () => {
    setSelectedDays(tempSelectedDays);
    setDayFilterVisible(false);
  };

  const applyCategoryFilters = () => {
    setSelectedCategories(tempSelectedCategories);
    setCategoryFilterVisible(false);
  };

  const resetTimeFilters = () => {
    setTempSelectedTimes([]);
  };

  const resetDayFilters = () => {
    setTempSelectedDays([]);
  };

  const resetCategoryFilters = () => {
    setTempSelectedCategories([]);
  };

  const removeTimeFilter = () => {
    setSelectedTimes([]);
  };

  const removeDayFilter = () => {
    setSelectedDays([]);
  };

  const removeCategoryFilter = () => {
    setSelectedCategories([]);
  };

  // Distinct meeting_category values present across the group's sessions —
  // populates the Categoria filter from real data instead of a hardcoded
  // taxonomy.
  const sessionCategoryOptions: CategoryOption[] = React.useMemo(() => {
    const seen = new Set<string>();
    const result: CategoryOption[] = [];
    for (const m of meetings) {
      const embedded = (m as any).sessions;
      // ONLY pull from meetings_sessions — ignore the legacy parent
      // meetings.meeting_category column.
      if (!Array.isArray(embedded)) continue;
      for (const s of embedded) {
        // List endpoint maps session rows to { id, weekday, time, period,
        // category } — read `category`, falling back to the raw column.
        const raw = String(s?.category || s?.meeting_category || '').trim();
        if (!raw || seen.has(raw)) continue;
        seen.add(raw);
        result.push({ key: raw, label: translateCategory(raw, t) });
      }
    }
    result.sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    return result;
  }, [meetings, t]);

  // Normalize a meeting's sessions[] or fall back to its flat
  // weekday/time/category cols so filters compose across both shapes.
  const getSessionsToCheck = (meeting: Meeting) => {
    const embedded = (meeting as any).sessions;
    if (Array.isArray(embedded) && embedded.length > 0) {
      // List endpoint shape: { id, weekday, time, period, category }.
      // Accept legacy meeting_time / meeting_category too.
      return embedded.map((s: any) => ({
        weekday: String(s.weekday || ''),
        meeting_time: String(s.time ?? s.meeting_time ?? ''),
        meeting_category: String(s.category ?? s.meeting_category ?? ''),
      }));
    }
    const days = (meeting.meeting_weekday || '')
      .split(/[;,]/)
      .map((d) => d.trim())
      .filter(Boolean);
    if (days.length === 0) {
      return [{
        weekday: '',
        meeting_time: meeting.meeting_time || '',
        meeting_category: meeting.meeting_category || '',
      }];
    }
    return days.map((d) => ({
      weekday: d,
      meeting_time: meeting.meeting_time || '',
      meeting_category: meeting.meeting_category || '',
    }));
  };

  // Filter meetings — time/day/category match against ANY session.
  const filteredMeetings = meetings.filter(meeting => {
    const sessions = getSessionsToCheck(meeting);

    if (selectedTimes.length > 0) {
      const timeMatch = sessions.some((s) => selectedTimes.includes(s.meeting_time));
      if (!timeMatch) return false;
    }

    if (selectedDays.length > 0) {
      const dayMatch = selectedDays.some((selectedDay) => {
        const needle = selectedDay.toLowerCase();
        return sessions.some((s) => (s.weekday || '').toLowerCase().includes(needle));
      });
      if (!dayMatch) return false;
    }

    if (selectedCategories.length > 0) {
      const catMatch = sessions.some((s) =>
        selectedCategories.includes(String(s.meeting_category || ''))
      );
      if (!catMatch) return false;
    }

    return true;
  }).sort((a, b) => {
    // Check if meetings are live (using the same logic as getMeetingTimeStatus)
    const timeStatusA = getMeetingTimeStatus(a.meeting_time, a.meeting_weekday, t);
    const timeStatusB = getMeetingTimeStatus(b.meeting_time, b.meeting_weekday, t);
    
    const isLiveA = timeStatusA.status === 'ongoing';
    const isLiveB = timeStatusB.status === 'ongoing';
    
    // Prioritize live meetings first
    if (isLiveA && !isLiveB) return -1;
    if (!isLiveA && isLiveB) return 1;
    
    // If both are live or both are not live, sort by proximity to current time
    const now = new Date();
    const currentTimeInMinutes = now.getHours() * 60 + now.getMinutes();
    
    const getMeetingTimeInMinutes = (meetingTime?: string): number => {
      if (!meetingTime || !meetingTime.includes(':')) return 0;
      const [hours, minutes] = meetingTime.split(':').map(Number);
      return (hours || 0) * 60 + (minutes || 0);
    };
    
    const timeA = getMeetingTimeInMinutes(a.meeting_time);
    const timeB = getMeetingTimeInMinutes(b.meeting_time);
    
    // Calculate time difference from current time
    // For meetings that haven't started yet today, use positive difference
    // For meetings that have passed today, add 24 hours (next day)
    const getDifferenceFromNow = (meetingTimeMinutes: number): number => {
      let difference = meetingTimeMinutes - currentTimeInMinutes;
      if (difference < 0) {
        difference += 24 * 60; // Add 24 hours for next occurrence
      }
      return difference;
    };
    
    const diffA = getDifferenceFromNow(timeA);
    const diffB = getDifferenceFromNow(timeB);
    
    // Sort by closest upcoming time
    return diffA - diffB;
  });

  const MeetingCard = ({ meeting }: { meeting: Meeting }) => {
    const { displayText, isLive } = getMeetingDisplayTime(meeting.meeting_time, meeting.meeting_weekday, t);
    const isPlus = !!meeting.plus_required;

    return (
      <TouchableOpacity 
        style={styles.meetingCard}
        onPress={() => handleMeetingPress(meeting)}
        activeOpacity={0.7}
      >
        <View style={[styles.meetingImageContainer, isPlus && styles.meetingImageContainerPlus]}>
          <ImageBackground
            source={require('../../assets/images/image_1763325486152.png')}
            style={styles.meetingImage}
            imageStyle={styles.meetingImageStyle}
          >
            {isPlus && (
              <View style={styles.meetingPlusBadgeWrapper}>
                <View style={styles.meetingPlusBadgeBlock}>
                  <Svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                    <Path d="M1.75524 5.7637L5.40815 9.7771C5.72535 10.1259 6.2741 10.1259 6.59125 9.7771L10.2447 5.7632C10.5502 5.42805 10.5852 4.92698 10.3298 4.55191L8.8587 2.39099C8.6763 2.12343 8.37275 1.96289 8.04875 1.96289H3.95555C3.63156 1.96289 3.32849 2.12294 3.14606 2.3905L1.67059 4.55191C1.4147 4.92649 1.44973 5.42805 1.75524 5.7637Z" stroke="#FFFFFF" strokeLinecap="round" strokeLinejoin="round" />
                    <Path d="M6.99847 1.96289L7.68197 4.55191C7.78117 4.92698 7.76757 5.42805 7.64887 5.7632L6.22887 9.7771C6.10577 10.1259 5.89222 10.1259 5.76912 9.7771L4.34912 5.7637C4.23042 5.42805 4.2168 4.92649 4.31653 4.55191L5.00197 1.96289" stroke="#FFFFFF" strokeLinecap="round" strokeLinejoin="round" />
                    <Path d="M1.50391 5.10156H10.4954" stroke="#FFFFFF" strokeLinecap="round" strokeLinejoin="round" />
                  </Svg>
                </View>
                <Svg style={{ position: 'absolute', top: 0, left: 24 }} width="8" height="8" viewBox="0 0 8 8" fill="none">
                  <Path d="M0 0H8C1.29032 0 0 2.83871 0 8V0Z" fill="#669AB7" />
                </Svg>
                <Svg style={{ position: 'absolute', top: 24, left: 0 }} width="8" height="8" viewBox="0 0 8 8" fill="none">
                  <Path d="M0 0V8C0 1.29032 2.83871 0 8 0H0Z" fill="#669AB7" />
                </Svg>
              </View>
            )}
          </ImageBackground>
        </View>
        <View style={styles.meetingContent}>
          <Text style={styles.meetingTitle} numberOfLines={2}>{meeting.meeting_name}</Text>
          <View style={styles.meetingTime}>
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path d="M10.1016 2.46143V4.44497" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M5.28516 2.46143V4.44497" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path fillRule="evenodd" clipRule="evenodd" d="M10.4566 3.37598L4.94039 3.37652C3.32005 3.37652 2.30859 4.27884 2.30859 5.93735V10.9302C2.30859 12.6149 3.32005 13.5381 4.94039 13.5381L10.4519 13.5376C12.077 13.5376 13.0837 12.6305 13.0837 10.9714V5.9368C13.0885 4.27829 12.0817 3.37598 10.4566 3.37598Z" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M5.01562 7.625H5.02229" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M5.01562 9.92773H5.02229" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M7.69141 7.625H7.69807" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M7.69141 9.92773H7.69807" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M10.3594 7.625H10.366" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <Path d="M10.3594 9.92773H10.366" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </Svg>
            <Text style={styles.meetingTimeText}>
              {displayText}
            </Text>
          </View>
        </View>
        <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <Path d="M6 3L11 8L6 13" stroke="#A19F97" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </Svg>
      </TouchableOpacity>
    );
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <PageHeader title="" onBack={() => router.back()} backgroundColor="#F9F8F3" />
        <View style={styles.divider} />

        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
          {/* Group Info Skeleton */}
          <View style={styles.groupInfoSection}>
            <View style={styles.groupInfo}>
              <SkeletonCircle size={24} />
              <Skeleton width="60%" height={28} />
              <Skeleton width="100%" height={16} />
              <Skeleton width="90%" height={16} />
            </View>

            {/* Filter Chips Skeleton */}
            <View style={styles.filtersRow}>
              <Skeleton width={100} height={34} borderRadius={17} />
              <Skeleton width={140} height={34} borderRadius={17} />
            </View>
          </View>

          {/* Meeting Cards Skeleton */}
          <View style={styles.meetingsSection}>
            {[1, 2, 3, 4, 5].map((i) => (
              <View key={i} style={styles.meetingCard}>
                <Skeleton width={64} height={64} borderRadius={12} />
                <View style={styles.meetingContent}>
                  <Skeleton width="80%" height={18} />
                  <Skeleton width="50%" height={12} style={{ marginTop: 4 }} />
                </View>
                <SkeletonCircle size={16} />
              </View>
            ))}
          </View>
        </ScrollView>
      </SafeAreaView>
    );
  }

  if (!group) {
    return (
      <SafeAreaView style={styles.container} edges={['top']}>
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{t('group.errors.notFound')}</Text>
          <TouchableOpacity style={styles.backButton} onPress={() => router.back()}>
            <Text style={styles.backButtonText}>Voltar</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // Use translated group if available, otherwise fall back to original
  const displayGroup = translatedGroup || group;

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <PageHeader title={displayGroup.name} onBack={() => router.back()} backgroundColor="#F9F8F3" />
      <View style={styles.divider} />

      {/* Scrollable Content */}
      <ScrollView 
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {/* Group Info Section */}
        <View style={styles.groupInfoSection}>
          <View style={styles.groupInfo}>
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path fillRule="evenodd" clipRule="evenodd" d="M6.37343 13.1602C4.5092 13.1616 2.99901 14.6739 3 16.5381C3.001 18.4023 4.5128 19.9129 6.37703 19.9124C8.24126 19.9119 9.75225 18.4005 9.75225 16.5363C9.75201 15.6405 9.39587 14.7815 8.76219 14.1483C8.12851 13.5151 7.26922 13.1597 6.37343 13.1602Z" stroke="#151515" strokeWidth="2" />
              <Path fillRule="evenodd" clipRule="evenodd" d="M17.6196 13.1602C15.7553 13.1616 14.2452 14.6739 14.2461 16.5381C14.2471 18.4023 15.7589 19.9129 17.6232 19.9124C19.4874 19.9119 20.9984 18.4005 20.9984 16.5363C20.9982 15.6405 20.642 14.7815 20.0083 14.1483C19.3747 13.5151 18.5154 13.1597 17.6196 13.1602Z" stroke="#151515" strokeWidth="2" />
              <Path fillRule="evenodd" clipRule="evenodd" d="M11.9984 4.08984C10.1342 4.09134 8.62401 5.60354 8.625 7.46777C8.626 9.332 10.1378 10.8426 12.002 10.8421C13.8663 10.8416 15.3773 9.3302 15.3773 7.46597C15.377 6.57017 15.0209 5.71117 14.3872 5.078C13.7535 4.44483 12.8942 4.08937 11.9984 4.08984Z" stroke="#151515" strokeWidth="2" />
            </Svg>
            <Text style={styles.groupName}>{displayGroup.name}</Text>
            <Text style={styles.groupDescription}>{displayGroup.description}</Text>
          </View>

          {/* Filter Chips */}
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.filtersRow}
          >
            <FilterChip
              icon={<ClockChipIcon />}
              label={selectedTimes.length > 0 ? selectedTimes[0] : t('group.filters.time')}
              active={selectedTimes.length > 0}
              onPress={handleTimeFilterPress}
              onClear={removeTimeFilter}
            />
            <FilterChip
              icon={<CalendarChipIcon />}
              label={selectedDays.length > 0 ? selectedDays[0] : t('group.filters.dayOfWeek')}
              active={selectedDays.length > 0}
              onPress={handleDayFilterPress}
              onClear={removeDayFilter}
            />
            <FilterChip
              icon={<CategoryChipIcon />}
              label={
                selectedCategories.length > 0
                  ? (selectedCategories.length === 1
                      ? translateCategory(selectedCategories[0], t)
                      : `${t('group.filters.category')} (${selectedCategories.length})`)
                  : t('group.filters.category')
              }
              active={selectedCategories.length > 0}
              onPress={handleCategoryFilterPress}
              onClear={removeCategoryFilter}
            />
          </ScrollView>
        </View>

        {/* Meetings List */}
        <View style={styles.meetingsSection}>
          {filteredMeetings.length > 0 ? (
            filteredMeetings.map((meeting) => (
              <MeetingCard key={meeting.id} meeting={meeting} />
            ))
          ) : (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>{t('group.errors.noMeetings')}</Text>
            </View>
          )}
        </View>
      </ScrollView>

      {/* Time Filter Drawer */}
      <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, elevation: 999, pointerEvents: timeFilterVisible ? 'auto' : 'none' }}>
        <FilterDrawer
          isVisible={timeFilterVisible}
          onClose={() => setTimeFilterVisible(false)}
          title={t('group.filters.time')}
          onApply={applyTimeFilters}
          onClear={resetTimeFilters}
          isApplyEnabled={tempSelectedTimes.length > 0}
          type="time"
        >
          <TimeFilterContent
            onSelectionChange={setTempSelectedTimes}
            currentSelection={tempSelectedTimes}
          />
        </FilterDrawer>
      </View>

      {/* Day Filter Drawer */}
      <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, elevation: 999, pointerEvents: dayFilterVisible ? 'auto' : 'none' }}>
        <FilterDrawer
          isVisible={dayFilterVisible}
          onClose={() => setDayFilterVisible(false)}
          title={t('group.filters.dayOfWeek')}
          onApply={applyDayFilters}
          onClear={resetDayFilters}
          isApplyEnabled={tempSelectedDays.length > 0}
          type="day"
        >
          <DayFilterContent
            onSelectionChange={setTempSelectedDays}
            currentSelection={tempSelectedDays}
          />
        </FilterDrawer>
      </View>

      {/* Category Filter Drawer */}
      <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, elevation: 999, pointerEvents: categoryFilterVisible ? 'auto' : 'none' }}>
        <FilterDrawer
          isVisible={categoryFilterVisible}
          onClose={() => setCategoryFilterVisible(false)}
          title={t('group.filters.category')}
          onApply={applyCategoryFilters}
          onClear={resetCategoryFilters}
          isApplyEnabled={tempSelectedCategories.length > 0}
          type="category"
        >
          <CategoryFilterContent
            options={sessionCategoryOptions}
            onSelectionChange={setTempSelectedCategories}
            currentSelection={tempSelectedCategories}
          />
        </FilterDrawer>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },
  divider: {
    height: 1,
    backgroundColor: '#E7EAED',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 100,
  },
  groupInfoSection: {
    paddingHorizontal: 24,
    paddingTop: 24,
    gap: 24,
  },
  groupInfo: {
    gap: 16,
  },
  groupName: {
    fontSize: 24,
    fontFamily: 'Sentient-Regular',
    color: '#151515',
    lineHeight: 28.8,
  },
  groupDescription: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '400',
    color: '#151515',
    lineHeight: 19.6,
  },
  filtersRow: {
    flexDirection: 'row',
    gap: 8,
  },
  filterChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderRadius: 17,
    borderWidth: 1,
    borderColor: '#DAD8CF',
    backgroundColor: '#F0EEE4',
  },
  filterChipText: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#3D3C38',
    lineHeight: 16.8,
  },
  activeFilterChip: {
    backgroundColor: '#F0EEE4',
    borderColor: '#151515',
  },
  activeFilterChipText: {
    color: '#151515',
  },
  meetingsSection: {
    paddingHorizontal: 24,
    paddingTop: 24,
    gap: 16,
  },
  meetingCard: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 24,
  },
  meetingImageContainer: {
    width: 64,
    height: 64,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#F0EEE4',
  },
  meetingImageContainerPlus: {
    borderColor: '#669AB7',
  },
  meetingPlusBadgeWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 32,
    height: 32,
  },
  meetingPlusBadgeBlock: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: 24,
    height: 24,
    backgroundColor: '#669AB7',
    borderBottomRightRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  meetingImage: {
    width: 64,
    height: 64,
  },
  meetingImageStyle: {
    borderRadius: 12,
  },
  meetingContent: {
    flex: 1,
    gap: 4,
  },
  meetingTitle: {
    fontSize: 14,
    fontFamily: 'Inter',
    fontWeight: '700',
    color: '#151515',
    lineHeight: 19.6,
  },
  meetingTime: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  meetingTimeText: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    color: '#7E7D75',
    lineHeight: 12,
  },
  emptyContainer: {
    paddingVertical: 40,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    fontFamily: 'Inter',
    fontWeight: '500',
    color: '#A19F97',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: 24,
  },
  errorText: {
    fontSize: 18,
    fontFamily: 'Inter',
    fontWeight: '500',
    color: '#151515',
  },
  backButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: '#015480',
    borderRadius: 12,
  },
  backButtonText: {
    fontSize: 16,
    fontFamily: 'Inter',
    fontWeight: '700',
    color: '#FFFFFF',
  },
});
